import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, getDoc, updateDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { getAnalytics, Analytics } from "firebase/analytics"; 
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut,
  User as FirebaseUser 
} from 'firebase/auth';
import { User, HealthTest, UserResponse } from '../types/firebase';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

console.log('Initializing Firebase...');

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

const ENABLE_ANALYTICS = true; 

let analytics: Analytics | null = null;

if (ENABLE_ANALYTICS && firebaseConfig.apiKey && firebaseConfig.apiKey.startsWith('AIza')) {
  try {
    analytics = getAnalytics(app);
    console.log("✅ Analytics initialized");
  } catch (e) {
    console.warn("⚠️ Analytics init failed:", e);
  }
} else {
  // 앱이 죽는 것을 막기 위해 로그만 남기고 null 처리
  console.log("⚠️ Analytics is DISABLED temporarily to prevent crash.");
}

// -----------------------------------------------------------

console.log('Firebase initialized with project:', firebaseConfig.projectId);

(async () => {
  try {
    // API Key가 유효할 때만 테스트 실행
    if (ENABLE_ANALYTICS) { 
        const testCollection = collection(db, 'test');
        // ... 연결 테스트 로직 (생략 가능)
    }
  } catch (error) {
    console.warn('⚠️ Firestore test warning:', error);
  }
})();

// 1. 구글 로그인
export const loginWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Google Login Error:", error);
    throw error;
  }
};

// 2. 카카오 로그인 
export const loginWithKakao = (hospitalId: string) => {
  const { Kakao } = window as any;
  
  // 카카오 초기화 확인 (이미 초기화되어 있지 않으면 초기화)
  if (!Kakao.isInitialized()) {
    Kakao.init(process.env.REACT_APP_KAKAO_JS_KEY);
  }

  // 카카오 인가 코드 요청 (authorize)
  // state 파라미터에 병원 ID를 담아 보내면, 나중에 그대로 돌려받음
  Kakao.Auth.authorize({
    redirectUri: process.env.REACT_APP_REDIRECT_URI,
    state: hospitalId,
  });
};

// 3. 네이버 로그인
export const loginWithNaver = async () => {
  alert("네이버 로그인은 별도의 설정이 필요합니다.");
  throw new Error("Naver login not implemented yet");
};

// 4. 로그아웃
export const logout = async () => {
  await signOut(auth);
};

// --- 기존 데이터 이전(Merge) 로직 ---

// 로그인 성공 후 익명 데이터를 계정 데이터로 이동
export const mergeAnonymousData = async (newUid: string, anonymousId: string) => {
  if (!anonymousId || newUid === anonymousId) return;

  try {
    const anonDocRef = doc(db, 'userResponses', anonymousId);
    const anonSnap = await getDoc(anonDocRef);

    if (anonSnap.exists()) {
      const anonData = anonSnap.data();
      const userDocRef = doc(db, 'userResponses', newUid);
      
      // 기존 데이터가 있다면 덮어쓰지 않고 병합하거나, 없다면 생성
      await setDoc(userDocRef, {
        ...anonData,
        userId: newUid,
        updatedAt: Timestamp.fromDate(new Date())
      }, { merge: true });

      console.log("✅ 데이터 이전 완료");
    }
  } catch (error) {
    console.error("Error merging data:", error);
  }
};

export const getUserData = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

export const updateUserData = async (userId: string, data: Partial<User>): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, data);
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

export const getHealthTests = async (): Promise<HealthTest[]> => {
  try {
    const testsSnapshot = await getDoc(doc(db, 'healthTests', 'list'));
    if (testsSnapshot.exists()) {
      return testsSnapshot.data().tests as HealthTest[];
    }
    return [];
  } catch (error) {
    console.error('Error fetching health tests:', error);
    throw error;
  }
};

export const getRecommendedTests = async (userId: string): Promise<HealthTest[]> => {
  try {
    const userData = await getUserData(userId);
    if (!userData?.recommendedTests) {
      return [];
    }

    const tests = await getHealthTests();
    return tests.filter(test => 
      userData.recommendedTests?.includes(test.id)
    );
  } catch (error) {
    console.error('Error fetching recommended tests:', error);
    throw error;
  }
};

export const saveUserResponse = async (userId: string, answers: Record<string, any>, language: string): Promise<void> => {
  try {
    const userResponse: UserResponse = {
      userId,
      responses: answers,
      createdAt: Timestamp.fromDate(new Date()),
      language
    };
    
    const userResponsesCollection = collection(db, 'userResponses');
    const userDocRef = doc(userResponsesCollection, userId);
    
    await setDoc(userDocRef, userResponse);
  } catch (error) {
    console.error('❌ Error in saveUserResponse:', error);
    throw error;
  }
};

export const updateUserResponse = async (userId: string, updates: Partial<UserResponse>): Promise<void> => {
  try {
    const userResponsesCollection = collection(db, 'userResponses');
    const userDocRef = doc(userResponsesCollection, userId);
    await setDoc(userDocRef, updates, { merge: true });
  } catch (error) {
    console.error('❌ Error updating user response:', error);
    throw error;
  }
};

export const getUserResponse = async (userId: string): Promise<UserResponse | null> => {
  try {
    const userResponsesCollection = collection(db, 'userResponses');
    const userDocRef = doc(userResponsesCollection, userId);
    const docSnap = await getDoc(userDocRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as UserResponse;
    }
    return null;
  } catch (error) {
    console.error('❌ Error getting user response:', error);
    throw error;
  }
};

export { db, auth, analytics };